import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import type { Step } from "./types";

interface ProductStepsProps {
  steps: Step[];
}

export function ProductSteps({ steps }: ProductStepsProps) {
  const { t } = useTranslation();

  return (
    <section className="py-12 lg:py-28 bg-secondary/50">
      <div className="container mx-auto px-4 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
            {t("productPages.howItWorks")}
          </h2>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-0 items-start">
            {steps.map((step, index) => (
              <div key={step.titleKey} className="flex items-start">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  className="text-center flex-1"
                >
                  <div className="w-14 h-14 rounded-full bg-coral text-coral-foreground flex items-center justify-center mx-auto mb-5 text-xl font-bold font-heading shadow-lg shadow-coral/20">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-bold font-subheading text-foreground mb-2">
                    {t(step.titleKey)}
                  </h3>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed max-w-[220px] mx-auto">
                    {t(step.descriptionKey)}
                  </p>
                </motion.div>

                {/* Arrow connector */}
                {index < steps.length - 1 && (
                  <div className="hidden md:flex items-center justify-center w-8 pt-5 flex-shrink-0">
                    <ArrowRight className="h-5 w-5 text-coral/40" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
